import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import CardSelect from '../components/ui/CardSelect';
import FormField from '../components/ui/FormField';
import HCaptchaField from '../components/ui/HCaptchaField';
import Button from '../components/ui/Button';
import Notice from '../components/ui/Notice';
import { entity } from '../content/en';
import { createContactFormMessage } from '../utils/contactFormMessageApi';
import { breadcrumbSchema } from '../components/ui/schema';

const TYPE_QUERY = ['property', 'business', 'innovation', 'partnership', 'other'];

const SubmitOpportunity = () => {
  const { t, i18n } = useTranslation('cy');
  const meta = t('submit.meta', { returnObjects: true });
  const location = useLocation();

  const initialType = useMemo(() => {
    const p = new URLSearchParams(location.search).get('type');
    return TYPE_QUERY.includes(p) ? p : null;
  }, [location.search]);

  const initialSituation = useMemo(() => {
    return new URLSearchParams(location.search).get('situation') || '';
  }, [location.search]);

  const [step, setStep] = useState(0);
  const [type, setType] = useState(initialType);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    organisation: '',
    role: '',
    relationship: '',
    preferred: '',
    summary: '',
    location: '',
    stage: '',
    seeking: '',
    materials: '',
    situation: initialSituation,
  });
  const [errors, setErrors] = useState({});
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);
  const [token, setToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const captchaRef = useRef(null);
  const stepHeadRef = useRef(null);

  const steps = t('submit.steps', { returnObjects: true });
  const typeOptions = t('submit.type.options', { returnObjects: true });
  const fields = t('submit.contact.fields', { returnObjects: true });
  const detailFields = t('submit.details', { returnObjects: true });

  const schema = [
    breadcrumbSchema([{ label: t('nav.home'), to: '/' }, { label: t('nav.submitCta'), to: '/submit-opportunity' }], i18n.language),
  ];

  useEffect(() => {
    stepHeadRef.current?.focus();
  }, [step]);

  useEffect(() => {
    if (initialType) setType(initialType);
    if (initialSituation) {
      setForm((f) => (f.situation === initialSituation ? f : { ...f, situation: initialSituation }));
    }
  }, [initialType, initialSituation]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validateStep = () => {
    const e = {};
    if (step === 0 && !type) e.type = t('submit.type.error');
    if (step === 1) {
      if (!form.name.trim()) e.name = fields.name.error;
      if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = fields.email.error;
      if (!form.relationship) e.relationship = fields.relationship.error;
      if (!form.preferred) e.preferred = fields.preferred.error;
    }
    if (step === 2 && !form.summary.trim()) e.summary = detailFields.summary.error;
    if (step === 4) {
      if (!consent1 || !consent2) e.consent = t('submit.review.error');
      if (!token) e.captcha = t('submit.review.captcha');
    }
    return e;
  };

  const next = () => {
    const e = validateStep();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setStep((s) => Math.min(s + 1, 4));
  };

  const back = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    const e = validateStep();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitting(true);
    setResult(null);
    const typeLabel = typeOptions.find((o) => o.value === type)?.title || type;

    const message = [
      `Opportunity type: ${typeLabel}`,
      form.situation ? `Situation: ${form.situation}` : '',
      form.summary ? `Summary: ${form.summary}` : '',
      form.location ? `Location / market: ${form.location}` : '',
      form.stage ? `Stage: ${form.stage}` : '',
      form.seeking ? `Looking for: ${form.seeking}` : '',
      form.materials ? `Supporting materials: ${form.materials}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    let res;
    try {
      res = await createContactFormMessage({
        fullname: form.name,
        email: form.email,
        phone: form.phone,
        message_subject: `Opportunity — ${typeLabel}`,
        message,
        captcha_token: token,
        page_info: typeof window !== 'undefined' ? window.location.href : '',
        ext_info: {
          source_form: 'submit_opportunity',
          form_type: 'opportunity',
          opportunity_type: type,
          situation: form.situation,
          organisation: form.organisation,
          role: form.role,
          relationship: form.relationship,
          preferred_contact: form.preferred,
        },
      });
    } finally {
      setSubmitting(false);
      captchaRef.current?.reset();
      setToken(null);
    }

    if (res.ok) {
      setResult({ status: 'success', reference: res.data && res.data.reference });
      setStep(5);
    } else {
      setResult({ status: 'error', message: res.message || t('submit.result.fail') });
      setStep(5);
    }
  };

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/submit-opportunity" schema={schema} />

      <PageHero
        id="submit-intro"
        image={`${process.env.PUBLIC_URL}/images/hero/page-submit.jpg`}
        eyebrow={t('submit.intro.eyebrow')}
        title={t('submit.intro.h1')}
        kicker={t('submit.intro.kicker')}
        breadcrumb={<Breadcrumbs items={[{ label: t('nav.submitCta') }]} />}
      >
        <ul className="cy-submit-notes">
          {t('submit.intro.notes', { returnObjects: true }).map((n) => (
            <li key={n.title}>
              <strong>{n.title}</strong>
              <span>{n.body}</span>
            </li>
          ))}
        </ul>
        <Notice variant="inline" className="cy-section-note">{t('submit.intro.sensitive')}</Notice>
      </PageHero>

      <section className="cy-section cy-section--alt cy-zone--doc cy-submit-flow">
        {step <= 4 && (
          <div className="cy-steps-bar">
            <div className="cy-container cy-container--form">
              <ol className="cy-steps">
                {steps.map((s, i) => (
                  <li key={s} aria-current={i === step ? 'step' : undefined}>
                    <span className="cy-steps__num">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
        <div className="cy-container cy-container--form">
          {step === 0 && (
            <div id="submit-type">
              <h2 className="cy-title" ref={stepHeadRef} tabIndex={-1}>{t('submit.type.h2')}</h2>
              <p className="cy-lede cy-step-lead">{t('submit.type.lead')}</p>
              <CardSelect
                name="opportunity-type"
                columns={2}
                value={type}
                onChange={setType}
                options={typeOptions.map((o) => ({
                  value: o.value,
                  title: o.title,
                  example: o.example,
                  summary: o.summary,
                }))}
              />
              {errors.type && <p className="cy-error" role="alert">{errors.type}</p>}
              <p className="cy-fineprint cy-section-note">
                {t('submit.type.notSure')}:{' '}
                <Link className="cy-link" to="/property-development#property-fit">{t('submit.type.criteria.property')}</Link>
                {' · '}
                <Link className="cy-link" to="/built-environment-investments#built-focus">{t('submit.type.criteria.business')}</Link>
                {' · '}
                <Link className="cy-link" to="/innovation-ventures#innovation-themes">{t('submit.type.criteria.innovation')}</Link>
              </p>
              {type === 'other' && (
                <Notice variant="info" className="cy-notice--follow">
                  {t('submit.intro.general')} <Link className="cy-link" to="/contact">{t('nav.contact')}</Link>
                </Notice>
              )}
              <div className="cy-form-nav">
                <Button onClick={next} ariaDisabled={!type}>{t('submit.type.continue')}</Button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div id="submit-contact">
              <h2 className="cy-title" ref={stepHeadRef} tabIndex={-1}>{t('submit.contact.h2')}</h2>
              <p className="cy-lede cy-step-lead">{t('submit.contact.lead')}</p>
              <FormField id="s-name" label={fields.name.label} help={fields.name.help} required error={errors.name}>
                <input id="s-name" className="cy-input" type="text" autoComplete="name" value={form.name} onChange={set('name')} aria-invalid={!!errors.name} />
              </FormField>
              <FormField id="s-email" label={fields.email.label} help={fields.email.help} required error={errors.email}>
                <input id="s-email" className="cy-input" type="email" autoComplete="email" inputMode="email" value={form.email} onChange={set('email')} aria-invalid={!!errors.email} />
              </FormField>
              <FormField id="s-phone" label={fields.phone.label} help={fields.phone.help} optional error={errors.phone}>
                <input id="s-phone" className="cy-input" type="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} />
              </FormField>
              <FormField id="s-org" label={fields.organisation.label} help={fields.organisation.help} optional>
                <input id="s-org" className="cy-input" type="text" autoComplete="organization" value={form.organisation} onChange={set('organisation')} />
              </FormField>
              <FormField id="s-role" label={fields.role.label} help={fields.role.help} optional>
                <input id="s-role" className="cy-input" type="text" value={form.role} onChange={set('role')} />
              </FormField>
              <FormField id="s-relationship" label={fields.relationship.label} help={fields.relationship.help} required error={errors.relationship}>
                <select id="s-relationship" className="cy-select" value={form.relationship} onChange={set('relationship')} aria-invalid={!!errors.relationship}>
                  <option value="">—</option>
                  {t('submit.contact.relationshipOptions', { returnObjects: true }).map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </FormField>
              <FormField id="s-preferred" label={fields.preferred.label} help={fields.preferred.help} required error={errors.preferred}>
                <select id="s-preferred" className="cy-select" value={form.preferred} onChange={set('preferred')} aria-invalid={!!errors.preferred}>
                  <option value="">—</option>
                  {t('submit.contact.preferredOptions', { returnObjects: true }).map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </FormField>
              <Notice variant="inline" className="cy-notice--before">{t('submit.contact.adviserNote')}</Notice>
              <div className="cy-form-nav">
                <Button variant="quiet" onClick={back}>{t('submit.contact.back')}</Button>
                <Button onClick={next}>{t('submit.contact.continue')}</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div id="submit-details">
              <h2 className="cy-title" ref={stepHeadRef} tabIndex={-1}>{t('submit.details.h2')}</h2>
              <p className="cy-fineprint cy-step-lead">{detailFields.hint}</p>
              <FormField id="s-summary" label={detailFields.summary.label} help={detailFields.summary.help} required error={errors.summary}>
                <input id="s-summary" className="cy-input" type="text" value={form.summary} onChange={set('summary')} aria-invalid={!!errors.summary} />
              </FormField>
              <FormField id="s-location" label={detailFields.location.label} help={detailFields.location.help} optional>
                <input id="s-location" className="cy-input" type="text" value={form.location} onChange={set('location')} />
              </FormField>
              <FormField id="s-stage" label={detailFields.stage.label} help={detailFields.stage.help} optional>
                <input id="s-stage" className="cy-input" type="text" value={form.stage} onChange={set('stage')} />
              </FormField>
              <FormField id="s-seeking" label={detailFields.seeking.label} help={detailFields.seeking.help} optional>
                <input id="s-seeking" className="cy-input" type="text" value={form.seeking} onChange={set('seeking')} />
              </FormField>
              <div className="cy-form-nav">
                <Button variant="quiet" onClick={back}>{detailFields.back}</Button>
                <Button onClick={next}>{detailFields.continue}</Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div id="submit-supporting-materials">
              <h2 className="cy-title" ref={stepHeadRef} tabIndex={-1}>{t('submit.materials.h2')}</h2>
              <p className="cy-lede cy-step-lead cy-step-lead--tight">{t('submit.materials.lead')}</p>
              <p className="cy-body">{t('submit.materials.examples')}</p>
              <Notice variant="inline" className="cy-notice--form-block">{t('submit.materials.warning')}</Notice>
              <FormField id="s-materials" label={t('submit.materials.linksLabel')} help={t('submit.materials.limit')} optional>
                <textarea id="s-materials" className="cy-textarea" rows={4} placeholder={t('submit.materials.linkPlaceholder')} value={form.materials} onChange={set('materials')} />
              </FormField>
              <div className="cy-form-nav">
                <Button variant="quiet" onClick={back}>{t('submit.materials.back')}</Button>
                <Button onClick={next}>{t('submit.materials.continue')}</Button>
              </div>
              <p className="cy-section-action">
                <button type="button" className="cy-btn cy-btn--quiet" onClick={() => setStep(4)}>
                  {t('submit.materials.skip')}
                </button>
              </p>
            </div>
          )}

          {step === 4 && (
            <form id="submit-review-consent" onSubmit={onSubmit} noValidate>
              <h2 className="cy-title" ref={stepHeadRef} tabIndex={-1}>{t('submit.review.h2')}</h2>
              <p className="cy-lede cy-step-lead">
                {t('submit.intro.overview')}
              </p>

              <dl className="cy-schedule cy-review">
                <div className="cy-schedule__row">
                  <dt>{t('submit.type.h2')}</dt>
                  <dd>
                    <p className="cy-schedule__what">{typeOptions.find((o) => o.value === type)?.title || type}</p>
                  </dd>
                </div>
                <div className="cy-schedule__row">
                  <dt>{t('submit.contact.h2')}</dt>
                  <dd>
                    <p className="cy-schedule__what">
                      {form.name} · {form.email}
                      {form.organisation ? ` · ${form.organisation}` : ''}
                    </p>
                  </dd>
                </div>
                <div className="cy-schedule__row">
                  <dt>{t('submit.details.h2')}</dt>
                  <dd>
                    <p className="cy-schedule__what">
                      {form.summary}
                      {form.location ? ` · ${form.location}` : ''}
                      {form.seeking ? ` · ${form.seeking}` : ''}
                    </p>
                  </dd>
                </div>
              </dl>

              {errors.consent && <p className="cy-error cy-error--follow" role="alert">{errors.consent}</p>}

              <label className="cy-checkbox cy-checkbox--spaced">
                <input type="checkbox" checked={consent1} onChange={(e) => setConsent1(e.target.checked)} />
                <span>{t('submit.review.confirm1')}</span>
              </label>
              <label className="cy-checkbox cy-checkbox--spaced">
                <input type="checkbox" checked={consent2} onChange={(e) => setConsent2(e.target.checked)} />
                <span>
                  {t('submit.review.confirm2')} <Link className="cy-link" to="/privacy">{t('ui.privacyPolicy')}</Link>
                </span>
              </label>

              <Notice variant="compliance" className="cy-notice--form">
                {t('submit.review.noNda')} <Link className="cy-link" to="/terms#terms-submissions">{t('ui.terms')}</Link>
              </Notice>

              <HCaptchaField ref={captchaRef} onToken={setToken} />
              {errors.captcha && <p className="cy-error cy-error--follow" role="alert">{errors.captcha}</p>}

              <div className="cy-form-nav">
                <Button variant="quiet" onClick={back} type="button">{t('submit.review.back')}</Button>
                <Button type="submit" ariaBusy={submitting} disabled={submitting}>
                  {submitting ? t('submit.review.submitting') : t('submit.review.submit')}
                </Button>
              </div>
            </form>
          )}

          {step === 5 && result && (
            <div
              className={`cy-panel cy-panel--${result.status === 'success' ? 'success' : 'error'}`}
              tabIndex={-1}
              role={result.status === 'success' ? 'status' : 'alert'}
            >
              <div className="cy-panel__icon">
                {result.status === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
              </div>
              <h2 className="cy-panel__title">
                {result.status === 'success' ? t('submit.result.successTitle') : t('submit.result.fail')}
              </h2>
              {result.status === 'success' ? (
                <>
                  <p className="cy-panel__body">{t('submit.result.successBody')}</p>
                  {result.reference && (
                    <p className="cy-panel__body">
                      {t('submit.result.reference')} <span className="cy-panel__ref">{result.reference}</span>
                    </p>
                  )}
                  <div className="cy-panel__actions">
                    <Link className="cy-link" to="/approach">{t('submit.result.next')}</Link>
                  </div>
                </>
              ) : (
                <>
                  <p className="cy-panel__body">{result.message}</p>
                  <div className="cy-panel__actions">
                    <Button onClick={() => { setResult(null); setStep(4); }}>{t('submit.result.retry')}</Button>
                    <a className="cy-link" href={entity.phoneHref}>{t('submit.result.call')} {entity.phone}</a>
                    <a className="cy-link" href={entity.emailHref}>{t('submit.result.email')} {entity.email}</a>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default SubmitOpportunity;
